import { Fragment, ReactNode } from "react";
import { Link, useMatches } from "react-router";
import * as s from "./Breadcrumb.css";

// ---------------------------------------------------------------------------
// <Breadcrumb> — route-driven crumb trail rendered just under the top nav.
//
// audit/21-visual-diff.md §12 calls out that the SPA had no breadcrumb at
// all — once a user clicked through to /deals/370000/items/12345 the only
// way "up" was the browser back button. Legacy had no breadcrumb either,
// but the SPA's much-richer left-nav makes the missing crumb more obvious.
//
// Pattern: each route defines `handle.crumb` in routesConfig.tsx. The
// crumb is either:
//   - a string ("Deals", "Customize Dashboard"), OR
//   - a (match) => ReactNode function for dynamic crumbs that need to
//     read route params (`#${match.params.dealId}`).
//
// useMatches returns matches in route-tree order; we filter to those with
// a crumb defined and render them as <Link>s, plus a final non-link entry
// for the leaf (current page). This keeps the trail self-updating —
// adding a new route just means adding a `handle: { crumb: ... }` entry.
// ---------------------------------------------------------------------------

// React Router's match shape includes whatever you put on `handle`. We
// declare a narrow contract here so call sites get type help instead of
// having to cast through `unknown`.
export interface CrumbMatch {
  pathname: string;
  params: Readonly<Record<string, string | undefined>>;
  data: unknown;
  handle: unknown;
}

export type CrumbValue = string | ((match: CrumbMatch) => ReactNode);

interface CrumbHandle {
  crumb?: CrumbValue;
}

const hasCrumb = (handle: unknown): handle is CrumbHandle =>
  typeof handle === "object" && handle !== null && "crumb" in handle;

const renderCrumb = (handle: CrumbHandle, match: CrumbMatch): ReactNode => {
  const { crumb } = handle;
  if (crumb === undefined) return null;
  if (typeof crumb === "function") return crumb(match);
  return crumb;
};

const Breadcrumb = () => {
  const matches = useMatches();

  // Filter to matches that opted in. The root layout match (no path
  // segment) is intentionally skipped — "Dashboard" reads weirdly when
  // every page starts with "Dashboard > …" and we want the crumb to be
  // genuinely useful, not noisy.
  const crumbs = matches
    .filter((m) => hasCrumb(m.handle))
    .map((m) => ({
      match: m as CrumbMatch,
      node: renderCrumb(m.handle as CrumbHandle, m as CrumbMatch),
    }))
    .filter((c) => c.node !== null && c.node !== undefined);

  // Don't render an empty bar — keeps the chrome quiet for screens where
  // no crumbs make sense (e.g. the root dashboard).
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={s.bar}>
      <ol className={s.list}>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <Fragment key={c.match.pathname}>
              <li className={s.item}>
                {isLast ? (
                  <span className={s.current} aria-current="page">
                    {c.node}
                  </span>
                ) : (
                  <Link to={c.match.pathname} className={s.link}>
                    {c.node}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li className={s.sep} aria-hidden="true">
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
